import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { CircleSlash, X } from 'lucide-react';

import {
    ColumnProductLightFragment,
    ColumnProductsFilterInput,
    ProductState,
    useGetColumnProductsQuery,
    useGetColumnQuery,
    useCreateZeroColumnInventoryStocksMutation,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';
import { EntityPageBreadcrumb, PageHeader } from 'components/PageHeader';
import { ActionBar } from 'components/ActionBar';
import { confirmPopup } from 'components/Popups/Confirm';

import { getColumnStocksColumns } from './constants/columnStocksColumns';

type ParamTypes = {
    columnId: string;
};

const defaultFilters: ColumnProductsFilterInput = {
    states: [ProductState.Regular, ProductState.Created],
};

export const ColumnStocks = () => {
    const { columnId = '' } = useParams<ParamTypes>();
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [createZeroColumnInventoryStocks] = useCreateZeroColumnInventoryStocksMutation();

    const {
        loading: columnLoading,
        data: columnData,
        error: columnError,
    } = useGetColumnQuery({
        variables: {
            _id: columnId,
        },
    });

    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
        fetchMore,
        refetch,
    } = useGetColumnProductsQuery({
        variables: {
            columnId,
            offset: 0,
            filters: defaultFilters,
        },
        skip: !columnId,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const hasMore = productsData?.columnProducts.hasMore ?? false;
    const columnProducts = useMemo(() => productsData?.columnProducts.columnProducts ?? [], [productsData]);

    const loadError = columnError || productsError;
    useEffect(() => {
        if (loadError) {
            toast.error('Une erreur est survenue lors de la récupération des produits de la colonne');
        }
    }, [loadError]);

    const columns = useMemo(() => getColumnStocksColumns(), []);

    const handleZeroInventory = async () => {
        try {
            const hasConfirmed = await confirmPopup({
                title: 'Confirmer',
                message: 'Êtes-vous sûr.e de vouloir créer un inventaire à zéro pour les produits suivants ?',
            });
            if (!hasConfirmed) return;
            const { data } = await createZeroColumnInventoryStocks({
                variables: {
                    columnProducts: selectedProductIds.map((productId) => ({ columnId, productId })),
                },
            });
            const result = data?.createZeroColumnInventoryStocks;
            if (!result?.success) {
                toast.error(result?.error ?? "Une erreur est survenue lors de la création de l'inventaire des zéros");
                return;
            }
            toast.success('Inventaire des zéros créé avec succès');
            setSelectedProductIds([]);
            await refetch();
        } catch {
            toast.error("Une erreur est survenue lors de la création de l'inventaire des zéros");
        }
    };

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => setSelectedProductIds([]),
                    hidden: !selectedProductIds.length,
                    dataTest: 'column-stocks-clear-selection',
                },
                {
                    icon: <CircleSlash />,
                    label: 'Inventaire des zéros',
                    onClick: handleZeroInventory,
                    hidden: !selectedProductIds.length,
                    dataTest: 'column-stocks-zero-inventory',
                },
            ],
        },
    ];

    if (columnLoading) {
        return (
            <Container>
                <Loader mode={LoaderModeType.Spin} />
            </Container>
        );
    }

    if (columnError || !columnData?.column || productsError) {
        return null;
    }

    const column = columnData.column;
    const site = column.site;
    const totem = site.totem;
    const organization = totem.organization;
    const columnGroup = column.microstoreColumn?.columnGroup;

    return (
        <Container>
            <PageHeader>
                <EntityPageBreadcrumb
                    organization={organization}
                    totem={totem}
                    site={site}
                    columnGroup={columnGroup}
                    column={column}
                    stocksView
                />
            </PageHeader>
            <ContentContainer>
                <Content>
                    <ListContainer>
                        <DataTable<ColumnProductLightFragment, ColumnProductsFilterInput>
                            columns={columns}
                            data={columnProducts}
                            filterMode="server"
                            getRowId={(row) => row.product._id}
                            getRowLink={(row) => `/column/${columnId}/stocks/${row.product._id}`}
                            hasMore={hasMore}
                            isLoading={productsLoading}
                            refetch={async (vars) => {
                                await refetch({ columnId, ...vars });
                            }}
                            fetchMore={async (opts) => {
                                await fetchMore({ variables: { columnId, ...opts.variables } });
                            }}
                            defaultFilters={defaultFilters}
                            enableSelection
                            selectedKeys={selectedProductIds}
                            onSelectionChange={setSelectedProductIds}
                            searchPlaceholder="Nom du produit"
                            searchFilterKey="searchValue"
                        />
                    </ListContainer>
                </Content>
                <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedProductIds.length} />
            </ContentContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: hidden;
`;

const ListContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;
